import React, {useEffect} from 'react';
import {
  Radio,
  FormControlLabel,
  RadioGroup,
  FormControl,
  Box,
  Typography,
} from '@mui/material';
import {t} from 'i18next';
import './IngestionRadioOptions.scss';
import '../../../../components/common/arr-radio/ArrRadio.scss';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';

interface Props {
  label: string;
  options: {label: string; value: string; tooltip: string; disabeld: boolean}[];
  defaultValue?: string;
  onChange?: (value: string) => void;
  onRun?: (value: string) => void;
  canRun: boolean;
  runLoading?: boolean;
}

const CustomCheckedIcon = () => {
  return (
    <span
      className="radio-icon radio-icon-checked"
      data-testid={`ingestion-radio-options-checked`}
    />
  );
};

const CustomUncheckedIcon = ({'data-testid': testId}: any) => {
  return (
    <span className="radio-icon radio-icon-unchecked" data-testid={testId} />
  );
};

const IngestionRadioOptions: React.FC<Props> = ({
  label,
  options,
  defaultValue,
  onChange,
  onRun,
  canRun,
  runLoading,
}) => {
  const [selectedValue, setSelectedValue] = React.useState<string>(
    defaultValue ?? ''
  );

  useEffect(() => {
    setSelectedValue(defaultValue ?? '');
  }, [defaultValue]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedValue(value);
    if (onChange) onChange(value);
  };

  const isRunButtonDisabled = !canRun || !selectedValue;

  return (
    <div
      className="analytics-index-run-options-container"
      data-testid={`ingestion-radio-options-run`}
    >
      <Typography
        className="typo-text subhead4_size title-label"
        tabIndex={0}
        data-testid={`ingestion-radio-options-label`}
      >
        {label}
      </Typography>
      <FormControl className="arr-radio arr-radio--large">
        <RadioGroup
          row
          name="radio-toggle-group"
          value={selectedValue}
          onChange={handleChange}
          className="radio-button-group"
        >
          {options.map((opt) => {
            return (
              <Box key={opt.value} display="flex" alignItems="center">
                <FormControlLabel
                  data-testid={opt.value}
                  value={opt.value}
                  control={
                    <Radio
                      disableRipple
                      className="custom-radio"
                      classes={{root: 'custom-radio'}}
                      icon={
                        <CustomUncheckedIcon
                          data-testid={`custom-unchecked-icon-${opt.label}`}
                        />
                      }
                      checkedIcon={
                        <CustomCheckedIcon
                          data-testid={`custom-checked-icon-${opt.label}`}
                        />
                      }
                      slotProps={{
                        input: {
                          'aria-label': opt.label,
                          'data-testid': `radio-${opt.label}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>,
                      }}
                      disabled={opt.disabeld}
                    />
                  }
                  label={opt.label}
                  className={'option-label'}
                  disabled={opt.disabeld}
                  tabIndex={0}
                />

                <ArrTooltipWrapper
                  trigger={
                    <InfoIcon
                      className="info-icon-label"
                      data-testid={`toggle-info-tooltip-${opt.label}`}
                      width={18}
                      height={18}
                      tabIndex={0}
                    />
                  }
                  title={opt.tooltip}
                  placement="top"
                >
                  {opt.tooltip}
                </ArrTooltipWrapper>
              </Box>
            );
          })}
        </RadioGroup>
      </FormControl>
      <Box>
        <ArrButton
          aria-label={t('ingestions.ingestionAnalytics.toggle.run')}
          type="submit"
          variant="primary"
          size="medium"
          disabled={isRunButtonDisabled}
          className="toggle-run-button"
          onClick={() => onRun?.(selectedValue)}
          loading={runLoading}
          dataTestId="ingestion-radio-options-run-button"
        >
          {t('ingestions.ingestionAnalytics.toggle.run')}
        </ArrButton>
      </Box>
    </div>
  );
};

export default IngestionRadioOptions;
